/**
 * ATO & SRO Summary View — 2-tab layout: Summary + Agent feed.
 *
 * The summary reads directly from the `atoSroEntries` table,
 * not from `summarizationTasks.output`.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  ArrowLeft,
  RotateCcw,
  Landmark,
  Bot,
  MessageSquare,
} from "lucide-react";
import { formatDuration } from "../shared";
import { StatusDot } from "../StatusBadges";
import { UnifiedFeed } from "../shared-panels";
import { SummaryChatSheet } from "../SummaryChatSheet";
import { ExportButton } from "@/ui/composed/ExportButton";
import type { AtoSroViewProps } from "./types";
import { AtoSroPanel } from "./AtoSroPanel";
import { buildAtoSroExport } from "./buildAtoSroExport";

export function AtoSroView({
  taskId,
  jobId,
  onBack,
  onRerun,
  isViewer,
}: AtoSroViewProps) {
  const [chatOpen, setChatOpen] = useState(false);

  const { data: task } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      { taskId },
    ),
  );

  // Canonical entries + rebuilt ledger movements feed the rich multi-sheet
  // export (the summary counts in task.output are only a fraction of the data).
  const { data: entries } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAtoSroEntriesForJob, {
      jobId,
    }),
  );
  const { data: ledgerRows } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAtoLedgerForJob, {
      jobId,
    }),
  );

  if (!task) return null;

  const isRunning = task.status === "running";
  const isCompleted = task.status === "completed";
  const isFailed = task.status === "failed";
  const isWaiting = task.status === "waiting";

  const output = task.output as
    | {
        totalAuthorities?: number;
        totalRunningBalance?: number;
        flaggedEntryCount?: number;
      }
    | undefined;

  return (
    <Tabs
      defaultValue={isRunning ? "feed" : "summary"}
      className="-m-6 flex flex-1 flex-col bg-muted px-6 pb-6 [overflow-anchor:none]"
    >
      {/* Sticky header + tabs */}
      <div className="sticky top-16 z-[9] -mx-6 bg-muted px-6 pb-2 pt-6">
        <div className="flex items-center gap-3 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <StatusDot status={task.status} />
            <h3 className="text-lg font-semibold">ATO & SRO Summary</h3>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {task.duration != null && (
              <Badge variant="outline" className="text-xs tabular-nums">
                {formatDuration(task.duration)}
              </Badge>
            )}
            {isCompleted && task.output && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChatOpen(true)}
                className="gap-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Chat
              </Button>
            )}
            {isCompleted && task.output && (
              <ExportButton
                content={buildAtoSroExport(entries ?? [], ledgerRows ?? [])}
                filename="ATO_SRO_Summary"
              />
            )}
            {(isRunning || isCompleted || isFailed) && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRerun}
                disabled={isViewer}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Re-run
              </Button>
            )}
          </div>
        </div>

        {!isWaiting && (
          <TabsList className="w-full">
            <TabsTrigger value="summary" className="gap-1.5">
              <Landmark className="h-3.5 w-3.5" />
              ATO & SRO
              {output?.totalAuthorities != null &&
                output.totalAuthorities > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 px-1 text-[10px]"
                  >
                    {output.totalAuthorities}
                  </Badge>
                )}
            </TabsTrigger>
            <TabsTrigger value="feed" className="gap-1.5">
              <Bot className="h-3.5 w-3.5" />
              Agent
              {isRunning && (
                <span className="relative ml-1 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        )}
      </div>

      {/* Waiting state */}
      {isWaiting && (
        <div className="flex flex-col items-center gap-3 py-16">
          <div className="rounded-full bg-muted/50 p-4">
            <Landmark className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">Ready to analyse</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Cross-references ATO/SRO documents, director disclosures, and bank
              transactions to identify tax authority liabilities and flags.
            </p>
          </div>
        </div>
      )}

      {/* Active / completed — 2 tabs */}
      {!isWaiting && (
        <>
          <TabsContent value="summary" className="mt-2">
            <AtoSroPanel
              jobId={jobId}
              taskId={taskId}
              isRunning={isRunning}
              isViewer={isViewer}
            />
          </TabsContent>

          <TabsContent value="feed" className="mt-2">
            <UnifiedFeed
              task={task}
              subAgents={[]}
              taskTree={task.workingMemory?.taskTree}
              isRunning={isRunning}
            />
          </TabsContent>
        </>
      )}

      {isCompleted && task.output && (
        <SummaryChatSheet
          open={chatOpen}
          onOpenChange={setChatOpen}
          taskId={taskId}
          jobId={jobId}
          agentName="ATO & SRO"
        />
      )}
      <AiDisclaimer className="mt-4 text-center" />
    </Tabs>
  );
}
